import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  FlexBox,
  Typography,
  Box,
  SvgIconButton,
  Drawer,
  Button,
  InputField,
  SvgIcon,
  color
} from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { selectors as configSelectors, actions as configActions, Configuration } from 'store/configurations';
import { MediaTypes } from '../../../types';

interface TransitionTimeDrawerProps {
  isOpen: boolean;
  mediaType: MediaTypes;
  configurationId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parentObj: any;
  configuration: Configuration;
  transitionTime: number;
  onClose: () => void;
}

export const TransitionTimeDrawer: React.FC<TransitionTimeDrawerProps> = (props: TransitionTimeDrawerProps) => {
  const { isOpen, mediaType, configurationId, transitionTime, parentObj, onClose } = props;
  const { configuration } = props.configuration;
  const { globalSettings } = configuration;

  const { pending, error, success } = useSelector(configSelectors.selectAction);
  const [updateConfiguration, resetAction, resetError] = useActions([
    configActions.updateConfiguration,
    configActions.resetAction,
    configActions.resetError
  ]);
  const [transitionTimeSec, setTransitionTime] = useState<number>(transitionTime);
  const [transitionTimeError, setTransitionTimeError] = useState('');
  const isSaveTransitionDisabled = Boolean(!transitionTimeSec || transitionTimeSec === transitionTime);

  const onTransitionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (error) {
      resetError();
    }
    setTransitionTimeError('');
    if (Number(value) >= 1 && Number(value) <= 10) {
      setTransitionTime(Number(value) || 0);
    } else {
      setTransitionTimeError('Slide time must be at least 1 second and no more than 10 seconds');
    }
  };

  useEffect(() => {
    if (!isOpen && error) {
      resetError();
    }
    setTransitionTimeError('');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (error) {
      setTransitionTimeError(error || '');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    if (success && isOpen) {
      resetAction();
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  const handleSave = () => {
    if (mediaType === MediaTypes.ATTRACT_IMAGE) {
      updateConfiguration({
        id: configurationId,
        configuration: {
          ...configuration,
          globalSettings: {
            ...globalSettings,
            attractLoop: { ...parentObj, transitionTime: transitionTimeSec as number }
          }
        }
      });
    }
  };

  return (
    <Drawer open={isOpen} onClose={onClose} hasBorderRadius>
      <FlexBox direction="column" padding="6 0" width={500} bg="white">
        <FlexBox align="center" justify="between" padding="0 6">
          <Typography fontSize={32} fontWeight={600}>
            Slideshow
          </Typography>
          <SvgIconButton appearance="transparent" onClick={onClose} data-testid="update-image-x">
            <SvgIcon name="exit_circle" size={40} />
          </SvgIconButton>
        </FlexBox>

        <Box flexOverride="none" fullWidth height={1} margin="6 0" sx={{ backgroundColor: '#EFEFEF' }} />
        <Box
          margin="0 6"
          width={400}
          sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        >
          <Typography fontSize={22} sx={{ textAlign: 'center' }}>
            Slide Transition Time
          </Typography>
          <InputField
            size="small"
            value={transitionTimeSec}
            onChange={onTransitionChange}
            type="number"
            data-testid="transition-input"
            align="center"
            autoFocus
            sx={{
              margin: '20px 0',
              fontSize: '16px',
              height: '38px !important',
              minWidth: '100px',
              padding: '0px 12px',
              textAlign: 'center',
              width: 'auto'
            }}
          />
        </Box>
        <Box margin="4 6" width={400} sx={{ textAlign: 'center' }}>
          <Box margin="0 3">
            {transitionTimeError && (
              <Box margin="2 0">
                <Typography color={color.RED}>{transitionTimeError}</Typography>
              </Box>
            )}
            <Button
              customSize="small"
              width={200}
              onClick={handleSave}
              isLoading={pending}
              disabled={isSaveTransitionDisabled}
            >
              Save
            </Button>
          </Box>
        </Box>
      </FlexBox>
    </Drawer>
  );
};
